import { resolveScheduleAuditDisplay } from "./scheduleAuditLabels";
import {
  buildTimelineFromScheduleContext,
  getFirstAppointmentAnchor,
} from "./scheduleAppointmentTimeline";
import { CHECK_STATUS, buildSchedulePreviewReport } from "./schedulePreviewChecklist";
import { resolveCheckImpact } from "./schedulePreviewImpacts";
import { SCHEDULE_PUBLISH_MIN_READINESS_PERCENT } from "./scheduleSaveGate";

const MAX_PRIORITY_ITEMS = 5;
const VISIT_TIME_POINT_LABEL = "Visit time point";

/** Publish gate: every non-adhoc visit must have a completed time point. */
export function areAllVisitTimePointsComplete(report) {
  const visits = (report?.visitReports || []).filter((visit) => !visit?.isAdhoc);
  if (!visits.length) return false;
  return visits.every((visit) => {
    const timePoint = (visit.items || []).find(
      (entry) => entry?.label === VISIT_TIME_POINT_LABEL,
    );
    return !!timePoint && timePoint.status === CHECK_STATUS.COMPLETE;
  });
}

function priorityRank(status) {
  if (status === CHECK_STATUS.BLOCKED) return 0;
  if (status === CHECK_STATUS.PENDING) return 1;
  return 2;
}

function collectPriorityActions(report, maxItems = MAX_PRIORITY_ITEMS) {
  if (!report) return { items: [], allItems: [], hiddenCount: 0 };

  const actions = [];

  const pushFlat = (entry, scope) => {
    if (
      entry.optionalForLive ||
      (entry.status !== CHECK_STATUS.PENDING &&
        entry.status !== CHECK_STATUS.BLOCKED)
    ) {
      return;
    }
    actions.push({
      rank: priorityRank(entry.status),
      scope,
      label: entry.label,
      message: entry.message || "",
      impact: resolveCheckImpact(entry.label, entry.status),
      impactPrefix: "If not completed",
      status: entry.status,
    });
  };

  (report.setupItems || []).forEach((entry) => pushFlat(entry, "Setup"));
  (report.visitStructureItems || []).forEach((entry) => pushFlat(entry, "Structure"));

  (report.visitReports || []).forEach((visit) => {
    const pending = (visit.items || []).filter(
      (entry) =>
        !entry.optionalForLive &&
        (entry.status === CHECK_STATUS.PENDING ||
          entry.status === CHECK_STATUS.BLOCKED),
    );
    if (!pending.length) return;

    const messages = pending
      .map((entry) => entry.message || entry.label)
      .filter(Boolean);
    const blocked = pending.some((entry) => entry.status === CHECK_STATUS.BLOCKED);

    actions.push({
      rank: blocked ? 0 : 1,
      scope: visit.visitName || "Visit",
      label: visit.visitName || "Visit",
      message: messages[0] || "",
      impact: messages[0]
        ? resolveCheckImpact(
            pending.find((entry) => (entry.message || entry.label) === messages[0])?.label ||
              pending[0]?.label,
            blocked ? CHECK_STATUS.BLOCKED : CHECK_STATUS.PENDING,
          )
        : "",
      extraCount: Math.max(0, messages.length - 1),
      status: blocked ? CHECK_STATUS.BLOCKED : CHECK_STATUS.PENDING,
    });
  });

  actions.sort((a, b) => a.rank - b.rank || a.scope.localeCompare(b.scope));

  const items = actions.slice(0, maxItems);
  return {
    items,
    allItems: actions,
    hiddenCount: Math.max(0, actions.length - items.length),
  };
}

const MAX_DRAFT_GAP_ITEMS = 12;

function collectDraftSetupGaps(report, maxItems = MAX_DRAFT_GAP_ITEMS) {
  const actions = [];

  const pushCheck = (entry, scope) => {
    if (
      entry.status !== CHECK_STATUS.PENDING &&
      entry.status !== CHECK_STATUS.BLOCKED
    ) {
      return;
    }
    actions.push({
      rank: entry.optionalForLive ? 2 : priorityRank(entry.status),
      scope,
      label: entry.label,
      message: entry.message || "",
      impact: resolveCheckImpact(entry.label, entry.status),
      impactPrefix: "If not completed",
      status: entry.status,
      optionalForLive: !!entry.optionalForLive,
      kind: "incomplete",
    });
  };

  (report?.setupItems || []).forEach((entry) => pushCheck(entry, "Setup"));
  (report?.visitStructureItems || []).forEach((entry) => pushCheck(entry, "Structure"));
  (report?.visitReports || []).forEach((visit) => {
    (visit.items || []).forEach((entry) => {
      pushCheck(entry, visit.visitName || "Visit");
    });
  });

  actions.sort(
    (a, b) =>
      a.rank - b.rank ||
      a.scope.localeCompare(b.scope) ||
      a.label.localeCompare(b.label),
  );

  const items = actions.slice(0, maxItems);
  return {
    items,
    allItems: actions,
    hiddenCount: Math.max(0, actions.length - items.length),
  };
}

function formatAnchorLabel(date) {
  return date.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildLiveHeadline(appointmentTimeline) {
  const anchorLabel =
    appointmentTimeline?.anchorLabel || formatAnchorLabel(getFirstAppointmentAnchor());
  return `Here's how appointments flow from ${anchorLabel} once the first booking is made.`;
}

function buildCompactHeadline({
  report,
  showFlowSummary,
  appointmentTimeline,
  canPublish = false,
}) {
  if (showFlowSummary) {
    return buildLiveHeadline(appointmentTimeline);
  }

  if (!report) {
    return "Save setup first to check readiness.";
  }

  const { summary } = report;
  const pct =
    summary.total > 0 ? Math.round((summary.complete / summary.total) * 100) : 0;

  if (summary.readyForLive) {
    return summary.optionalPending
      ? `${pct}% complete — required checks are done. You can publish now.`
      : `${pct}% ready — you can publish now.`;
  }

  if (canPublish) {
    const left = (summary.pending || 0) + (summary.blocked || 0);
    if (left > 0) {
      return `Visit time points are set — you can publish. ${left} other item${
        left === 1 ? "" : "s"
      } below are still recommended.`;
    }
    return "Visit time points are set — you can publish now.";
  }

  const left = summary.pending + summary.blocked;
  if (summary.blocked > 0) {
    return `${pct}% ready — fix ${summary.blocked} blocker${
      summary.blocked === 1 ? "" : "s"
    } and ${summary.pending} pending item${summary.pending === 1 ? "" : "s"} first.`;
  }

  return `${pct}% ready — set a time point on every visit before publishing.`;
}

function summarizeLatestChange(auditLog = []) {
  const row = auditLog?.[0];
  if (!row) return "";

  const display = resolveScheduleAuditDisplay(row);
  const parts = [display.actionDisplay];
  if (display.detailDisplay) {
    parts.push(display.detailDisplay);
  }
  return parts.join(" · ");
}

export async function evaluateSchedulePublishReadiness({
  wizard,
  cohort,
  scheduleCreated,
  visitBuilderMode,
  visitCohorts,
  studyScheduleId,
  organizationId,
  createdBy,
  minPercent = SCHEDULE_PUBLISH_MIN_READINESS_PERCENT,
}) {
  if (!studyScheduleId || !organizationId) {
    return {
      allowed: false,
      percent: 0,
      readyForLive: false,
      canPublish: false,
      priorityActions: [],
      allPriorityActions: [],
      hiddenCount: 0,
      error: "Save setup and visits first.",
    };
  }

  const report = await buildSchedulePreviewReport({
    wizard,
    cohort,
    scheduleCreated,
    visitBuilderMode,
    visitCohorts,
    studyScheduleId,
    organizationId,
    createdBy,
  });

  const summary = report?.summary || {};
  const requiredTotal = summary.requiredTotal ?? summary.total ?? 0;
  const requiredComplete = summary.requiredComplete ?? summary.complete ?? 0;
  const percent =
    requiredTotal > 0 ? Math.round((requiredComplete / requiredTotal) * 100) : 100;
  const { items: priorityActions, allItems, hiddenCount } = collectPriorityActions(
    report,
    3,
  );
  const canPublish = areAllVisitTimePointsComplete(report);
  const readyForLive = Boolean(summary.readyForLive);

  return {
    allowed: canPublish,
    percent,
    readyForLive,
    canPublish,
    priorityActions,
    allPriorityActions: allItems,
    hiddenCount,
    pending: summary.requiredPending ?? summary.pending ?? 0,
    blocked: summary.requiredBlocked ?? summary.blocked ?? 0,
    minPercent,
  };
}

export function buildPublishBlockedMessage({
  percent = 0,
  minPercent = SCHEDULE_PUBLISH_MIN_READINESS_PERCENT,
  priorityActions = [],
  hiddenCount = 0,
  pending = 0,
  blocked = 0,
  error = "",
}) {
  if (error) return error;

  const parts = ["Set a time point on every visit before publishing."];

  if (blocked > 0) {
    parts.push(`${blocked} blocker${blocked === 1 ? "" : "s"} must be fixed first.`);
  } else if (pending > 0) {
    parts.push(`${pending} item${pending === 1 ? "" : "s"} still incomplete.`);
  }

  if (priorityActions.length) {
    const top = priorityActions
      .map((item) => `${item.scope}: ${item.message || item.label}`)
      .join("; ");
    parts.push(`Start with: ${top}.`);
  }

  if (hiddenCount > 0) {
    parts.push(`+${hiddenCount} more on the Go live checklist.`);
  }

  if (percent > 0 && minPercent > 0) {
    parts.push(`Overall readiness is ${percent}%.`);
  }

  return parts.join(" ");
}

export async function buildScheduleActivityAiSummary({
  auditLog = [],
  wizard,
  cohort,
  scheduleCreated,
  visitBuilderMode,
  visitCohorts,
  studyScheduleId,
  organizationId,
  createdBy,
  isScheduleLive = false,
}) {
  let report = null;
  let reportError = "";

  if (studyScheduleId && organizationId) {
    try {
      report = await buildSchedulePreviewReport({
        wizard,
        cohort,
        scheduleCreated,
        visitBuilderMode,
        visitCohorts,
        studyScheduleId,
        organizationId,
        createdBy,
      });
    } catch (err) {
      reportError = err?.message || "Could not evaluate schedule readiness.";
    }
  } else {
    reportError = "Save setup first.";
  }

  const fullyReady = Boolean(report?.summary?.readyForLive);
  const canPublish = areAllVisitTimePointsComplete(report);
  // Publish button only needs time points; full readiness still drives the live timeline view.
  const canGoLive = canPublish;
  const showFlowSummary = isScheduleLive || fullyReady;
  const { items: priorityActions, allItems, hiddenCount } = collectDraftSetupGaps(report);

  let appointmentTimeline = null;
  if (showFlowSummary && studyScheduleId && organizationId) {
    try {
      appointmentTimeline = await buildTimelineFromScheduleContext({
        wizard,
        visitBuilderMode,
        visitCohorts,
        cohort,
        setupFlags: wizard?.setupFlags,
        studyScheduleId,
        organizationId,
        createdBy,
      });
    } catch {
      const anchorDate = getFirstAppointmentAnchor();
      appointmentTimeline = {
        anchorDate,
        anchorLabel: formatAnchorLabel(anchorDate),
        visits: [],
      };
    }
  }

  return {
    summaryMode: showFlowSummary ? "flow" : "draft",
    headline: buildCompactHeadline({
      report,
      showFlowSummary,
      appointmentTimeline,
      canPublish,
    }),
    priorityActions,
    allPriorityActions: allItems,
    hiddenCount,
    latestChange: summarizeLatestChange(auditLog),
    readiness: report?.summary || null,
    canGoLive,
    canPublish,
    fullyReady,
    isScheduleLive,
    reportError,
    appointmentTimeline,
  };
}
